import {
  Component,
  Output,
  EventEmitter,
  inject,
  effect,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import { PushDestinationsListStore } from './push-destinations-list.store';
import { PushDestinationDisplay } from './push-destination-display.mapper';
import {
  PushDestinationDialogComponent,
  PushDestinationDialogData,
} from '../push-destination-dialog';

export { PushDestinationDisplay } from './push-destination-display.mapper';

/**
 * PushDestinationsComponent — displays and manages a project's push destinations.
 *
 * A thin OnPush view over {@link PushDestinationsListStore}: the store owns all
 * data loading, the display rows, and the test-result state (signals/`resource()`),
 * while this component only renders, opens the create/edit dialog and the delete
 * confirm, and re-emits the count to the parent surface.
 *
 * The Add button lives in the embedding surface (the Settings tab header); call
 * {@link addDestination} to trigger the create flow. Mirrors the connections
 * reference pattern (SCRUM-761).
 */
@Component({
  selector: 'app-push-destinations',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatDividerModule,
  ],
  templateUrl: './push-destinations.component.html',
  styleUrls: ['./push-destinations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PushDestinationsListStore],
})
export class PushDestinationsComponent {
  readonly projectId = input<number | null>(null);

  @Output() destinationsCountChange = new EventEmitter<number>();

  private readonly store = inject(PushDestinationsListStore);
  private readonly dialogService = inject(DialogService);
  private readonly dialog = inject(MatDialog);

  /** Display rows (retains previous rows during refetch). */
  readonly displayDestinations = this.store.displayDestinations;
  readonly loading = this.store.loading;
  readonly testingDestinationId = this.store.testingDestinationId;
  readonly testResult = this.store.testResult;

  constructor() {
    // Push the component input into the store, which drives the resource.
    effect(() => this.store.setProjectId(this.projectId()));
    // Mirror the total count to the parent.
    effect(() => this.destinationsCountChange.emit(this.store.count()));
  }

  onActionMenu(destination: PushDestinationDisplay, action: string): void {
    switch (action) {
      case 'edit':
        this.onEdit(destination);
        break;
      case 'test':
        this.testDestination(destination);
        break;
      case 'toggle':
        this.toggleEnabled(destination);
        break;
      case 'delete':
        this.onDelete(destination);
        break;
    }
  }

  /**
   * Open the add-destination dialog and reload on success. Exposed so the
   * embedding surface (Settings tab header) triggers the same flow.
   */
  addDestination(): void {
    const projectId = this.store.projectId();
    if (!projectId) {
      console.warn('No valid project ID available for adding destination');
      return;
    }
    this.openDialog({ projectId, mode: 'create' });
  }

  refreshDestinations(): void {
    this.store.reload();
  }

  clearTestResult(): void {
    this.store.clearTestResult();
  }

  private onEdit(destination: PushDestinationDisplay): void {
    const projectId = this.store.projectId();
    if (!projectId) {
      console.warn('No valid project ID available for editing destination');
      return;
    }
    this.openDialog({
      projectId,
      mode: 'edit',
      destination: destination.originalData,
    });
  }

  private openDialog(data: PushDestinationDialogData): void {
    this.dialog
      .open(PushDestinationDialogComponent, {
        data,
        width: '640px',
        disableClose: false,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.destination) {
          this.store.reload();
        }
      });
  }

  private testDestination(destination: PushDestinationDisplay): void {
    void this.store.testDestination(destination);
  }

  private toggleEnabled(destination: PushDestinationDisplay): void {
    this.store
      .toggleEnabled(destination)
      .catch((error) =>
        console.error('Error toggling push destination:', error),
      );
  }

  private onDelete(destination: PushDestinationDisplay): void {
    this.dialogService
      .confirm({
        title: 'Delete Push Destination',
        message: `Are you sure you want to delete "${destination.name}"? This action cannot be undone.`,
        confirmCaption: 'Delete',
        cancelCaption: 'Cancel',
        isDestructive: true,
      })
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.store
          .deleteDestination(destination)
          .catch((error) =>
            console.error('Error deleting push destination:', error),
          );
      });
  }
}
